import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { ArrowRight, Zap, Shield, Brain, Calendar, Users, Clock, Settings } from 'lucide-react';

const ALL_LOGOS_IMAGE = "https://customer-assets.emergentagent.com/job_peak-mastery/artifacts/ont6xb82_All%20logos.png";

export default function WorkshopsPage() {
  const topicsRef = useRef(null);
  const isTopicsInView = useInView(topicsRef, { once: true, margin: "-100px" });
  const includesRef = useRef(null);
  const isIncludesInView = useInView(includesRef, { once: true, margin: "-100px" });

  const topicBuckets = [
    {
      icon: Zap,
      title: "Signal Over Noise",
      description: "Help your team cut through distraction and make better decisions faster.",
      outcomes: ["Improved focus and productivity", "Better decision-making frameworks", "Reduced mental fatigue"],
    },
    {
      icon: Shield,
      title: "Psychological Safety",
      description: "Build a culture where people take intelligent risks and speak up.",
      outcomes: ["Higher team engagement", "Increased innovation", "Stronger trust and collaboration"],
    },
    {
      icon: Brain,
      title: "Peak Performance Systems",
      description: "Design systems for sustainable high performance across your organization.",
      outcomes: ["Consistent high output", "Reduced burnout", "Scalable performance culture"],
    },
  ];

  const workshopIncludes = [
    { icon: Calendar, text: "Pre-engagement discovery call" },
    { icon: Clock, text: "Half-day to multi-day formats" },
    { icon: Users, text: "Interactive exercises & breakouts" },
    { icon: Settings, text: "Customized to your organization" },
  ];

  return (
    <main data-testid="workshops-page">
      {/* Hero */}
      <section className="pt-24 lg:pt-32 pb-14 lg:pb-20 bg-[#F4F1EC]">
        <div className="container-peak">
          <div className="max-w-3xl mx-auto text-center px-4 sm:px-0">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <span className="section-tag block mb-4 lg:mb-6">Workshops & Training</span>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-light leading-[1.1] mb-4 lg:mb-6 text-[#1F2A44]">
                Transform how your team{' '}
                <em className="not-italic text-[#D97904]">thinks and performs</em>
              </h1>
              <p className="text-[#5A6B7A] text-base lg:text-lg mb-8 lg:mb-10 max-w-2xl mx-auto">
                Customized programs designed for lasting impact. From half-day workshops to multi-month transformations.
              </p>
              <a href="#request" className="btn-primary inline-flex items-center justify-center gap-3">
                Request a Program
                <ArrowRight size={16} />
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Topic Buckets */}
      <section ref={topicsRef} className="py-14 lg:py-24 bg-white">
        <div className="container-peak">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isTopicsInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto mb-10 lg:mb-16"
          >
            <span className="section-tag block mb-4 lg:mb-6">Training Topics</span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light leading-[1.15] text-[#1F2A44]">
              Choose your <em className="not-italic text-[#D97904]">focus area</em>
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-5 lg:gap-8 max-w-5xl mx-auto">
            {topicBuckets.map((topic, index) => (
              <motion.div
                key={topic.title}
                initial={{ opacity: 0, y: 30 }}
                animate={isTopicsInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.1 * index }}
                className="bg-[#F4F1EC] p-5 lg:p-8 border border-[#E6E1DA] hover:border-[#D97904] transition-all card-hover"
              >
                <div className="w-10 h-10 lg:w-12 lg:h-12 flex items-center justify-center border border-[#E6E1DA] bg-white mb-4 lg:mb-6">
                  <topic.icon className="w-4 h-4 lg:w-5 lg:h-5 text-[#D97904]" />
                </div>
                <h3 className="font-['Cormorant_Garamond'] text-lg lg:text-2xl text-[#1F2A44] mb-2 lg:mb-3">
                  {topic.title}
                </h3>
                <p className="text-[#5A6B7A] text-sm mb-4 lg:mb-5">
                  {topic.description}
                </p>
                <div className="space-y-2">
                  {topic.outcomes.map((outcome, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs lg:text-sm text-[#1F2A44]">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#D97904] mt-1.5 flex-shrink-0" />
                      {outcome}
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-8 lg:mt-12">
            <a href="#request" className="btn-ghost inline-flex items-center gap-3">
              Request a Custom Workshop
              <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section ref={includesRef} className="py-16 lg:py-24 bg-[#F4F1EC]">
        <div className="container-peak">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={isIncludesInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6 }}
            >
              <span className="section-tag block mb-4 lg:mb-6">What's Included</span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light leading-[1.15] mb-4 lg:mb-6 text-[#1F2A44]">
                A standard workshop <em className="not-italic text-[#D97904]">includes</em>
              </h2>
              <p className="text-[#5A6B7A] text-sm lg:text-base mb-6">
                Every program is designed for measurable outcomes.
              </p>
              <a href="#request" className="btn-primary inline-flex items-center gap-3">
                Get Started
                <ArrowRight size={16} />
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={isIncludesInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="bg-white p-6 lg:p-8 border border-[#E6E1DA]"
            >
              <div className="space-y-4 lg:space-y-5">
                {workshopIncludes.map((item, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#F4F1EC] flex-shrink-0">
                      <item.icon className="w-4 h-4 text-[#D97904]" />
                    </div>
                    <span className="text-[#1F2A44] text-sm lg:text-base pt-2">{item.text}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Logos */}
      <section className="py-10 lg:py-14 bg-[#F4F1EC] border-y border-[#E6E1DA]">
        <div className="container-peak">
          <p className="text-center text-[10px] lg:text-[11px] tracking-[0.3em] uppercase text-[#5A6B7A] mb-6 lg:mb-8 font-['Syne']">
            Trusted by World-Class Organizations
          </p>
          <div className="max-w-4xl mx-auto">
            <img
              src={ALL_LOGOS_IMAGE}
              alt="Our clients"
              className="w-full h-auto object-contain"
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="request" className="py-16 lg:py-24 bg-[#1F2A44]">
        <div className="container-peak">
          <div className="max-w-2xl mx-auto text-center">
            <span className="section-tag block mb-4 lg:mb-6">Get Started</span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light leading-[1.15] mb-4 lg:mb-6 text-[#F4F1EC]">
              Ready to transform your team?
            </h2>
            <p className="text-[#F4F1EC]/70 text-sm lg:text-base mb-8 lg:mb-10">
              Tell us about your organization and goals. We'll design a program that delivers real results.
            </p>
            <a
              href="https://calendar.app.google/evsn9LsubajagFF3A"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary inline-flex items-center gap-3"
            >
              Book a Call
              <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
